import React from "react"
import PropTypes from "prop-types"
import Immutable from "immutable"

// constants
import {
    AVAILABLE_TO_EVERYONE,
    READ_LIMITED_TO_TEAMS,
    READ_JUST_ME,
} from "shared/imports/sharedConstants"

/**
 * This helper functions returns the toggle value as a function of the inputs below
 * @name getLimitToTeamsGridToggleValue
 * @function
 * @param {Boolean} isEditing - Whether we are editing this object
 * @param {Boolean} teamUrisArray[String] - An array of URI's of the form ["/api/team/123/", "/api/team/9/"]
 * @param {Boolean} defaultLimitedToTeams[String] - An array of URI's of the form ["/api/team/123/", "/api/team/9/"]
 *
 * @returns {Integer} - One of AVAILABLE_TO_EVERYONE, READ_LIMITED_TO_TEAMS, and READ_JUST_ME integers
 */
export const getLimitToTeamsGridToggleValue = ({
    isEditing,
    teamUrisArray = [],
    defaultLimitedToTeams,
    isLimitedToCreator,
    isInitialStateSet = false,
    shouldShowAvailableToEveryone = true,
    isLimitedToTeamsEnabled,
}) => {
    if (isLimitedToCreator) {
        return READ_JUST_ME
    } else if (!shouldShowAvailableToEveryone && isLimitedToTeamsEnabled) {
        return READ_LIMITED_TO_TEAMS
    } else if (!shouldShowAvailableToEveryone) {
        return READ_JUST_ME
    }
    // if we are creating, the Access toggle should be determined by whether the user has default_limited_to_teams set up
    if (isInitialStateSet && !isEditing) {
        return Boolean(defaultLimitedToTeams.length) ? READ_LIMITED_TO_TEAMS : AVAILABLE_TO_EVERYONE // eslint-disable-line no-extra-boolean-cast
    } else {
    // if we are editing, then the Access toggle should be Available to Everyone if there are no teams selected
        return Boolean(teamUrisArray.length) ? READ_LIMITED_TO_TEAMS : AVAILABLE_TO_EVERYONE // eslint-disable-line no-extra-boolean-cast
    }
}

// This component is used to render the basic Form structure as well as control whether the
// Team select should be shown by toggling between making the object created either Available to everyone
// at Organization or Limited to Teams
class LimitedToTeamsFormWrapper extends React.Component {

    constructor(props) {
        super(props)
        const { isEditing, teamUrisArray, isLimitedToCreator, isReduxForm, reduxFormKey, shouldResetSection } = props

        // for redux-forms, we have to reset the section that corresponds to the limited_to_Teams Field
        shouldResetSection && isReduxForm && isEditing && props.resetSection && props.resetSection(reduxFormKey, "limited_to_teams")
        this.state = {
            limitToTeamsGridToggleVal: getLimitToTeamsGridToggleValue({
                isEditing,
                teamUrisArray,
                defaultLimitedToTeams: Userdata.default_limited_to_teams,
                isLimitedToCreator,
                isInitialStateSet: true,
                shouldShowAvailableToEveryone: this.props.shouldShowAvailableToEveryone,
            })
        }

    }

    // if we switch modes, or switch to edit a different object we need to make sure we update internal state
    UNSAFE_componentWillReceiveProps = (nextProps) => {
        // if the page is just refreshed without seguing, reduxForm has to pass
        // down the initial values for the teams. Thus, we check if the teams array
        // has values and is different from the default (empty array) to load in
        // the initial props.
        const hasNewNonEmptyTeams = (
            nextProps.teamUrisArray
            && nextProps.teamUrisArray.length
            && nextProps.teamUrisArray !== this.props.teamUrisArray
        ) || nextProps.isLimitedToCreator !== this.props.isLimitedToCreator

        if (this.props.isMobile || hasNewNonEmptyTeams || !nextProps.shouldShowAvailableToEveryone) {
            this.setState(state => ({
                ...state,
                limitToTeamsGridToggleVal: getLimitToTeamsGridToggleValue({
                    ...nextProps,
                    defaultLimitedToTeams: Userdata.default_limited_to_teams,
                })
            }))
        }
    }

    // When clicking on "Limited to Teams", we need to set a default value so componentWillReceiveProps() doesn't reset the form
    // back in the case where the user has no default_limited_to_teams
    getLimitedToTeamsForOnChange = () => {
        if (this.props.initialTeamUris) {
            return this.props.initialTeamUris
        } else if (Userdata.default_limited_to_teams.length) {
            return Userdata.default_limited_to_teams
        } else if (this.props.possibleChoices.length) {
            return [this.props.possibleChoices[0].resource_uri]
        }
        return []
    }

    /**
     * Handle on change from the GridToggle component
     * @name handleOnChange
     * @function
     * @param {Number} limitToTeamsGridToggleVal - an enum with the following schema:
     *   0 - should not limit to teams
     *   1 - should limit to teams
     *   2 - should limit only to user
     * Use constants from forms/components/constants to define above
     */
    handleOnChange = (limitToTeamsGridToggleVal) => {
        const shouldLimitToTeams = limitToTeamsGridToggleVal === 1

        // change the state of this component to show/hide the GridToggleSelect
        this.setState(state => ({...state, shouldLimitToTeams, limitToTeamsGridToggleVal }))
        const { canLimitToCreator, reduxFormKey } = this.props
        switch (limitToTeamsGridToggleVal) {
            case AVAILABLE_TO_EVERYONE:
                // clear the limited to teams if we the user clicked on Available to everyone and clear the
                // is_limited_to_creator field if the form supports the Just Me option
                if (this.props.isReduxForm) {
                    this.props.change(reduxFormKey, "limited_to_teams", Immutable.fromJS([]))
                    canLimitToCreator && this.props.change(reduxFormKey, "is_limited_to_creator", false)
                } else {
                    this.props.onChange({
                        limited_to_teams: [],
                        ...(canLimitToCreator ? { is_limited_to_creator: false } : {}),
                        ...this.props
                    })
                }
                break
            case READ_LIMITED_TO_TEAMS:
                // redux forms manage state differently. Use redux-form action creators to change state
                if (this.props.isReduxForm) {
                    this.props.change(reduxFormKey, "limited_to_teams", Immutable.fromJS(this.getLimitedToTeamsForOnChange()))
                    canLimitToCreator && this.props.change(reduxFormKey, "is_limited_to_creator", false)
                } else { // for non redux forms, a required onChange is called
                    this.props.onChange({
                        limited_to_teams: this.getLimitedToTeamsForOnChange(),
                        ...(canLimitToCreator ? { is_limited_to_creator: false } : {}),
                        ...this.props
                    })
                }
                break

            case READ_JUST_ME:
                if (this.props.isReduxForm) {
                    this.props.change(reduxFormKey, "limited_to_teams", Immutable.fromJS([]))
                    canLimitToCreator && this.props.change(reduxFormKey, "is_limited_to_creator", true)
                } else {
                    this.props.onChange({
                        limited_to_teams: [],
                        ...(canLimitToCreator ? { is_limited_to_creator: true } : {}),
                        ...this.props
                    })
                }
                break
            default:
                console.warn("An option was passed that this component does not support. check the values you are passing to enableLimitedToTeamsOptions")
                // this case should not happen
        }
    }

    render() {
        const { LimitedToTeamsFormWrapperComponent } = this.props

        return (
            <LimitedToTeamsFormWrapperComponent
                formLabel={this.props.formLabel}
                shouldParentManageFormStep={this.props.shouldParentManageFormStep}
                shouldRenderFormSection={this.props.shouldRenderFormSection}
                step={this.props.step}
                canLimitToCreator={this.props.canLimitToCreator}
                enableLimitedToTeamsOptions={this.props.enableLimitedToTeamsOptions}
                limitToTeamsGridToggleVal={this.state.limitToTeamsGridToggleVal}
                handleOnChange={this.handleOnChange}
                {...this.props}
            >
                {this.props.children}
            </LimitedToTeamsFormWrapperComponent>
        )
    }
}

LimitedToTeamsFormWrapper.propTypes = {
    // the children is the Limited to Teams toggle that is passed through this form wrapper
    children: PropTypes.any,
    // this a configuration object for the GridToggle that selects between Available to everyone in org / Limited to Teams
    enableLimitedToTeamsOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    // the string that gets rendered
    formLabel: PropTypes.string.isRequired,

    // whether this form supports limiting to "Just Me"
    canLimitToCreator: PropTypes.bool,

    // whether this object is limited to creator. Only applicable if canLimitToCreator is true
    isLimitedToCreator: PropTypes.bool,

    // whether or not this belongs to a redux form or a regular, non-redux form
    isReduxForm: PropTypes.bool,

    // an Array of Objects of possible choices that the GridToggle should render
    possibleChoices: PropTypes.array,
    // the step in the form this should component falls in
    step: PropTypes.number.isRequired,

    // whether we need a parent component to manage the FormStep to include additional components in the form
    shouldParentManageFormStep: PropTypes.bool,
    // if this form should render at all. Useful when limited to teams don't apply in specific cases that can be
    // controlled by the parent form
    shouldRenderFormSection: PropTypes.bool.isRequired,

    // list of Team URIs representing an initial selection of teams
    // if this prop is passed, this will be the initial team selection in the component
    initialTeamUris: PropTypes.arrayOf(PropTypes.string),
    // whether or not to resetSection. E.g., Dashboard Edit Form does not need to reset
    shouldResetSection: PropTypes.bool,
    // whether or not we want to show the "Available to Everyone" option, default is true
    shouldShowAvailableToEveryone: PropTypes.bool,

    // whether or not the user has teams, if the user cannot access any teams then we can't limit to teams
    isLimitedToTeamsEnabled: PropTypes.bool,
}

LimitedToTeamsFormWrapper.defaultProps = {
    canLimitToCreator: false,
    isLimitedToCreator: false,
    isReduxForm: false,
    possibleChoices: [],
    shouldParentManageFormStep: false,
    shouldResetSection: true,
    shouldShowAvailableToEveryone: true,
}

export default LimitedToTeamsFormWrapper
